import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaSave, FaArrowLeft, FaTimes, FaBuilding, FaUser, FaGlobe, FaPlus, FaMapMarkerAlt } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import ConfirmationModal from '../components/ConfirmationModal';
import vendorService from '../services/vendorService';
import userService from '../services/userService';
import branchService from '../services/branchService';
import { useAuth } from '../context/AuthContext';

function VendorEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isEdit] = useState(!!id);
  const [deletingBranch, setDeletingBranch] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    website: '',
    social_profiles: [],
    status: 'inactive',
    owner_id: user?.id || '',
    referred_by: '',
    verified_by: '',
    verified_at: null
  });

  const [vendorOwner, setVendorOwner] = useState(null);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerSuggestions, setOwnerSuggestions] = useState([]);
  const [ownerSearching, setOwnerSearching] = useState(false);
  const [showOwnerEditor, setShowOwnerEditor] = useState(false);

  const [socialProfileInput, setSocialProfileInput] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [coverPreview, setCoverPreview] = useState('');

  // Branch management state
  const [branches, setBranches] = useState([{
    name: '',
    location: '',
    address: '',
    phone: '',
    whatsapp: '',
    notes: '',
    is_primary: true
  }]);

  // Build storage base from env (no trailing slash)
  const storageBase = (process.env.REACT_APP_STORAGE_URL || '').replace(/\/$/, '');
  const makeSrc = (val) => {
    if (!val) return null;
    if (typeof val !== 'string') return null;
    if (val.startsWith('http') || val.startsWith('//')) return val;
    if (val.startsWith('/')) return `${storageBase}${val}`;
    // treat as filename
    return storageBase ? `${storageBase}/uploads/vendors/${val}` : val;
  };

  const fetchVendor = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await vendorService.getById(id);

      setFormData({
        name: data.name || '',
        website: data.website || '',
        social_profiles: Array.isArray(data.social_profiles) ? data.social_profiles : [],
        status: data.status || 'inactive',
        owner_id: data.owner?.id || user?.id || '',
        referred_by: data.added_by?.username || '',
        verified_by: data.verified_by?.username || '',
        verified_at: data.verified_at || null
      });
        // populate new contact/display fields when editing
        setFormData(prev => ({
          ...prev,
          phone1: data.phone1 || '',
          phone2: data.phone2 || '',
          whatsapp: data.whatsapp || '',
          logo: data.logo || '',
          cover: data.cover || '',
          location: data.location || '',
          address: data.address || '',
          verify_badge: data.verify_badge || false
        }));
        setVendorOwner(data.owner || null);
        if (data.owner) setOwnerSearch(data.owner.username + (data.owner.email ? ` (${data.owner.email})` : ''));
        
        // Fetch branches for this vendor
        try {
          const branchesResponse = await branchService.getVendorBranches(id);
          if (branchesResponse && Array.isArray(branchesResponse.branches) && branchesResponse.branches.length > 0) {
            setBranches(branchesResponse.branches.map(b => ({
              id: b.id,
              name: b.name || '',
              location: b.location || '',
              address: b.address || '',
              phone: b.phone || '',
              whatsapp: b.whatsapp || '',
              notes: b.notes || '',
              is_primary: b.is_primary || false
            })));
          }
        } catch (branchErr) {
          console.warn('Failed to load branches:', branchErr);
          // Keep default single empty branch if fetch fails
        }
    } catch (err) {
      setError('Failed to load vendor');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => {
    fetchVendor();
  }, [fetchVendor]);

  // Prevent non-admin users from opening the admin create page
  useEffect(() => {
    if (!isEdit && user && user.role !== 'admin') {
      // Redirect non-admins away from the create page
      navigate('/vendors');
    }
  }, [isEdit, user, navigate]);

  // Debounced user search for owner selector
  useEffect(() => {
    const q = ownerSearch?.trim();
    if (!q || q.length < 2) {
      setOwnerSuggestions([]);
      setOwnerSearching(false);
      return;
    }

    let cancelled = false;
    setOwnerSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await userService.getAll({ q, per_page: 10 });
        if (!cancelled) {
          // API may return { items, data } or an array
          const list = Array.isArray(res) ? res : res.items || res.data || res.users || [];
          setOwnerSuggestions(list);
        }
      } catch (err) {
        console.error('User search failed', err);
        if (!cancelled) setOwnerSuggestions([]);
      } finally {
        if (!cancelled) setOwnerSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [ownerSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (name === 'logoFile') {
      setFormData(prev => ({ ...prev, logoFile: file }));
      setLogoPreview(URL.createObjectURL(file));
    } else if (name === 'coverFile') {
      setFormData(prev => ({ ...prev, coverFile: file }));
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const handleAddSocialProfile = () => {
    if (socialProfileInput.trim() && !formData.social_profiles.includes(socialProfileInput.trim())) {
      setFormData({
        ...formData,
        social_profiles: [...formData.social_profiles, socialProfileInput.trim()]
      });
      setSocialProfileInput('');
    }
  };

  const handleRemoveSocialProfile = (index) => {
    const newProfiles = formData.social_profiles.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      social_profiles: newProfiles
    });
  };

  // Branch handlers
  const handleBranchChange = (index, field, value) => {
    const newBranches = [...branches];
    newBranches[index][field] = value;
    
    // If setting a branch as primary, unset others
    if (field === 'is_primary' && value) {
      newBranches.forEach((b, i) => {
        if (i !== index) b.is_primary = false;
      });
    }
    
    setBranches(newBranches);
  };

  const handleAddBranch = () => {
    setBranches([...branches, {
      name: '',
      location: '',
      address: '',
      phone: '',
      whatsapp: '',
      notes: '',
      is_primary: false
    }]);
  };

  const handleRemoveBranch = (index) => {
    if (branches.length === 1) {
      setError('At least one branch is required');
      return;
    }
    
    setBranchToDelete(index);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteBranch = async () => {
    if (branchToDelete === null) return;
    
    const index = branchToDelete;
    const branchToRemove = branches[index];
    
    // If this is an existing branch (has id), delete it from the API
    if (branchToRemove.id && isEdit) {
      setDeletingBranch(true);
      
      try {
        await branchService.delete(id, branchToRemove.id);
      } catch (err) {
        console.error('Error deleting branch:', err);
        setError('Failed to delete branch: ' + (err.response?.data?.error || err.message));
        setDeletingBranch(false);
        setShowDeleteConfirm(false);
        setBranchToDelete(null);
        return;
      }
      
      setDeletingBranch(false);
    }
    
    const newBranches = branches.filter((_, i) => i !== index);
    // If we removed the primary branch, make the first one primary
    if (branchToRemove.is_primary && newBranches.length > 0) {
      newBranches[0].is_primary = true;
    }
    setBranches(newBranches);
    setShowDeleteConfirm(false);
    setBranchToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        social_profiles: formData.social_profiles || [],
        branches: branches.filter(b => b.name.trim()).map(b => ({
          id: b.id,
          name: b.name.trim(),
          location: b.location?.trim() || '',
          address: b.address?.trim() || '',
          phone: b.phone?.trim() || '',
          whatsapp: b.whatsapp?.trim() || '',
          notes: b.notes?.trim() || '',
          is_primary: b.is_primary || false
        }))
      };

      const hasFiles = !!(formData.logoFile || formData.coverFile);

      if (hasFiles) {
        const fd = new FormData();
        Object.keys(submitData).forEach((key) => {
          const val = submitData[key];
          if (val === undefined || val === null) return;
          if (key === 'logoFile' || key === 'coverFile') return;
          if (Array.isArray(val) || typeof val === 'object') {
            fd.append(key, JSON.stringify(val));
          } else {
            fd.append(key, val);
          }
        });
        if (formData.logoFile) fd.append('logo', formData.logoFile);
        if (formData.coverFile) fd.append('cover', formData.coverFile);

        if (isEdit) {
          await vendorService.update(id, fd);
        } else {
          if (user?.role !== 'admin') {
            setError('Only administrators can create vendors from the dashboard');
            setSaving(false);
            return;
          }
          await vendorService.create(fd);
        }

        navigate('/vendors');
      } else {
        if (isEdit) {
          await vendorService.update(id, submitData);
          navigate('/vendors');
        } else {
          if (user?.role !== 'admin') {
            setError('Only administrators can create vendors from the dashboard');
          } else {
            await vendorService.create(submitData);
            navigate('/vendors');
          }
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError(`Failed to ${isEdit ? 'update' : 'create'} vendor: ${errorMessage}`);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getPageTitle = () => {
    if (isEdit) return 'Edit Vendor';
    return 'Add New Vendor';
  };

  const getPageSubtitle = () => {
    if (isEdit) return `Editing ${formData.name || `vendor #${id}`}`;
    return 'Create a new vendor/company';
  };

  const getSubmitButtonText = () => {
    if (saving) return 'Saving...';
    if (isEdit) return 'Update Vendor';
    return 'Create Vendor';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title={getPageTitle()}
        subtitle={getPageSubtitle()}
      />

      <Row className="mb-3">
        <Col>
          <Button variant="secondary" onClick={() => navigate('/vendors')}>
            <FaArrowLeft className="me-2" />
            Back to Vendors
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Referral flow is handled in the main application (user profile), not the dashboard */}

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h5 className="fw-semibold mb-4">
                  <FaBuilding className="me-2" />
                  Company Information
                </h5>

                <Form.Group className="mb-3">
                  <Form.Label>Company Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter company name"
                  />
                  <Form.Text className="text-muted">
                    The official name of the company/vendor
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <FaGlobe className="me-1" />
                    Website
                  </Form.Label>
                  <Form.Control
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                  <Form.Text className="text-muted">
                    Company website URL (optional)
                  </Form.Text>
                </Form.Group>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="text"
                        name="phone1"
                        value={formData.phone1 || ''}
                        onChange={handleChange}
                        placeholder="Primary phone number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Alternate Phone</Form.Label>
                      <Form.Control
                        type="text"
                        name="phone2"
                        value={formData.phone2 || ''}
                        onChange={handleChange}
                        placeholder="Alternate phone number"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mt-2">
                      <Form.Label>WhatsApp</Form.Label>
                      <Form.Control
                        type="text"
                        name="whatsapp"
                        value={formData.whatsapp || ''}
                        onChange={handleChange}
                        placeholder="WhatsApp number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    {user?.role === 'admin' && (
                      <Form.Group className="mt-2">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                        >
                          <option value="inactive">Inactive</option>
                          <option value="active">Active</option>
                          <option value="pending">Pending</option>
                        </Form.Select>
                        <Form.Text className="text-muted">Only admins can set vendor status</Form.Text>
                      </Form.Group>
                    )}
                  </Col>
                </Row>

                <Row className="g-3 align-items-center">
                  <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Logo</Form.Label>
                        <Form.Control
                          type="file"
                          name="logoFile"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                        <Form.Text className="text-muted">Upload a square logo (PNG, JPG)</Form.Text>

                      </Form.Group>
                                                                      {(() => {
                          const src = logoPreview || makeSrc(formData.logo);
                          return src ? (
                            <div className="mb-2">
                              <img src={src} alt="logo" style={{ width: 'auto', height: 48, objectFit: 'cover', borderRadius: 8 }} />
                            </div>
                          ) : null;
                        })()}
                    </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cover Image</Form.Label>

                      <Form.Control
                        type="file"
                        name="coverFile"
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      <Form.Text className="text-muted">Upload a wide cover image (JPG, PNG)</Form.Text>
                    </Form.Group>
                     {(() => {
                        const src = coverPreview || makeSrc(formData.cover);
                        return src ? (
                          <div className="mb-2">
                            <img src={src} alt="cover" style={{ width: '100%', height: 48, objectFit: 'cover', borderRadius: 8 }} />
                          </div>
                        ) : null;
                      })()}
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="location"
                    value={formData.location || ''}
                    onChange={handleChange}
                    placeholder="City, State, Country"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="address"
                    value={formData.address || ''}
                    onChange={handleChange}
                    placeholder="Full postal address"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Social Media Profiles</Form.Label>
                  <div className="d-flex gap-2 mb-2">
                    <Form.Control
                      type="url"
                      placeholder="https://twitter.com/company"
                      value={socialProfileInput}
                      onChange={(e) => setSocialProfileInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSocialProfile())}
                    />
                    <Button
                      type="button"
                      variant="outline-primary"
                      onClick={handleAddSocialProfile}
                      disabled={!socialProfileInput.trim()}
                    >
                      Add
                    </Button>
                  </div>
                  {formData.social_profiles.length > 0 && (
                    <div className="mt-2">
                      {formData.social_profiles.map((profile, index) => (
                        <Badge
                          key={index}
                          bg="secondary"
                          className="me-2 mb-2 p-2"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRemoveSocialProfile(index)}
                        >
                          {profile}
                          <FaTimes className="ms-2" size={10} />
                        </Badge>
                      ))}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Add social media profile URLs (Twitter, Facebook, LinkedIn, etc.)
                  </Form.Text>
                </Form.Group>

                
              </Card.Body>
            </Card>

            {/* Branches Section */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-semibold mb-0">
                    <FaMapMarkerAlt className="me-2" />
                    Company Branches
                  </h5>
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddBranch}
                  >
                    <FaPlus className="me-1" /> Add Branch
                  </Button>
                </div>

                {branches.map((branch, index) => (
                  <Card key={index} className="mb-3 border">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Branch #{index + 1}</h6>
                        <div className="d-flex gap-2 align-items-center">
                          <Form.Check
                            type="checkbox"
                            id={`branch-primary-${index}`}
                            label="Primary"
                            checked={branch.is_primary}
                            onChange={(e) => handleBranchChange(index, 'is_primary', e.target.checked)}
                          />
                          {branches.length > 1 && (
                            <Button
                              type="button"
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveBranch(index)}
                            >
                              <FaTimes />
                            </Button>
                          )}
                        </div>
                      </div>

                      <Row className="g-3 mb-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Branch Name</Form.Label>
                            <Form.Control
                              type="text"
                              value={branch.name}
                              onChange={(e) => handleBranchChange(index, 'name', e.target.value)}
                              placeholder="Main Office, North Branch"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Location</Form.Label>
                            <Form.Control
                              type="text"
                              value={branch.location}
                              onChange={(e) => handleBranchChange(index, 'location', e.target.value)}
                              placeholder="City, State (for Maps)"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          type="text"
                          value={branch.address}
                          onChange={(e) => handleBranchChange(index, 'address', e.target.value)}
                          placeholder="Full street address"
                        />
                      </Form.Group>

                      <Row className="g-3 mb-3">
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                              type="text"
                              value={branch.phone}
                              onChange={(e) => handleBranchChange(index, 'phone', e.target.value)}
                              placeholder="+1234567890"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>WhatsApp</Form.Label>
                            <Form.Control
                              type="text"
                              value={branch.whatsapp}
                              onChange={(e) => handleBranchChange(index, 'whatsapp', e.target.value)}
                              placeholder="+1234567890"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                              type="text"
                              value={branch.notes}
                              onChange={(e) => handleBranchChange(index, 'notes', e.target.value)}
                              placeholder="Additional info"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}

                <Alert variant="info" className="mb-0">
                  <small>
                    <strong>Note:</strong> Add branch locations to help customers find your company. 
                    The primary branch will be displayed first.
                  </small>
                </Alert>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>


            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h6 className="fw-semibold mb-3">Actions</h6>

                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {getSubmitButtonText()}
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {getSubmitButtonText()}
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={() => navigate('/vendors')}
                    disabled={saving}
                  >
                    <FaTimes className="me-2" />
                    Cancel
                  </Button>
                </div>

                {user?.role === 'admin' && (
                  <div className="mt-3 pt-3 border-top">
                    <Form.Check
                      type="checkbox"
                      id="verify_badge"
                      name="verify_badge"
                      label="Set verified badge"
                      checked={!!formData.verify_badge}
                      onChange={(e) => setFormData(prev => ({ ...prev, verify_badge: e.target.checked }))}
                    />
                  </div>
                )}


              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-4">
              <Card.Body>
                <h6 className="fw-semibold mb-3">
                  <FaUser className="me-2" />
                  Owner Information
                </h6>
                <hr />

                <div className="text-muted small mb-2">
                  <p>
                    <strong>Company Owner:</strong>{' '}
                    {vendorOwner
                      ? `${vendorOwner.username} (${vendorOwner.email || 'no-email'})`
                      : 'Not assigned'}
                  </p>
                </div>

                {(user?.role === 'admin' || vendorOwner?.id === user?.id) && (
                  <Form.Group className="mb-3">
                    <Form.Label>Owner</Form.Label>

                    {!showOwnerEditor ? (
                      <div className="d-flex gap-2">
                        <Button variant="outline-primary" size="sm" onClick={() => { setShowOwnerEditor(true); setOwnerSearch(''); setOwnerSuggestions([]); }}>
                          Change Owner
                        </Button>
                        <Form.Text className="text-muted align-self-center">Current owner ID: {formData.owner_id || 'none'}</Form.Text>
                      </div>
                    ) : (
                      <>
                        <Form.Control
                          type="text"
                          placeholder="Search users by username or email..."
                          value={ownerSearch}
                          onChange={(e) => setOwnerSearch(e.target.value)}
                        />

                        {ownerSearching && <div className="small text-muted mt-1">Searching...</div>}

                        {ownerSuggestions.length > 0 && (
                          <div className="border rounded mt-1 bg-white" style={{ maxHeight: 200, overflowY: 'auto', zIndex: 1000 }}>
                            {ownerSuggestions.map((u) => (
                              <div
                                key={u.id}
                                role="button"
                                onClick={() => {
                                  setFormData((prev) => ({ ...prev, owner_id: u.id }));
                                  setVendorOwner(u);
                                  setOwnerSearch(u.username + (u.email ? ` (${u.email})` : ''));
                                  setOwnerSuggestions([]);
                                  setShowOwnerEditor(false);
                                }}
                                className="px-2 py-2 hover-bg-light"
                                style={{ cursor: 'pointer' }}
                              >
                                <strong>{u.username}</strong>
                                <div className="small text-muted">{u.email}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="d-flex gap-2 mt-2">
                          <Button variant="primary" size="sm" onClick={() => setShowOwnerEditor(false)}>
                            Done
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => { setShowOwnerEditor(false); setOwnerSearch(''); setOwnerSuggestions([]); }}>
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </Form.Group>
                )}

                {/* Referral-specific messaging removed from dashboard */}
              </Card.Body>
            </Card>
            {/* Referral and verify card removed per dashboard policy; verification remains admin-only via vendor list */}
          </Col>
        </Row>
      </Form>

      <ConfirmationModal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setBranchToDelete(null);
        }}
        onConfirm={confirmDeleteBranch}
        title="Delete Branch"
        message={`Are you sure you want to delete this branch${branchToDelete !== null && branches[branchToDelete]?.name ? ` "${branches[branchToDelete].name}"` : ''}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deletingBranch}
      />
    </Container>
  );
}

export default VendorEditPage;